import NO05, { NO05StatusStat } from '../../models/NO05';
import { KONTANT_PAYMENT_METHODS, DIREKTE_ORDER_TYPES, isPlukkOrdre, hasBkCode, isReturnToSupplierBk } from './no05StatusGroups';

const PREFERRED_STATUS_ORDER = [
    'FERDIGMELDT.', 'FMELD. FAKT. KS-OPPGJ.', 'FERDIG PLUKKET', 'PRINTET.', 'PAKKET.',
    'FERDIG REGISTRERT.', 'VENTER GODKJENNING', 'PÅBEGYNT,IKKE FULLFØRT',
    'REG. KRED-STOPP IKKE P', 'LAGEROVF. (F.MELDT)', 'VERKSTEDSORD.(F.MELDT)',
    'UTGÅRMELDT.', 'KORR. ETTER F.MELDING.', 'RESTREG. (F.MELDT)', 'RET. TIL LEV.(F.MELDT)',
    'DELFERDIGMELDT.', 'PÅBEGYNT SAMLEORDRE', 'SLUPPET FORHÅNDSORDRE', 'UFULLSTENDIG INNLAGT',
];

// Combined BRF% the way ORVI computes it (CICSORVI.CBL BEREGN-SUM, line 1062): total
// dekningsbidrag in kroner over total dekningsgrunnlag in kroner.
//
//     BRF% = (Σ brfOmsetning − Σ brfKost) / Σ brfOmsetning × 100
//
// Not an average of the per-order BRF%-column: weighting those percentages by `belop` looks like
// another way to get there but isn't — `belop` includes surcharge and Frakt, which brfOmsetning
// excludes (decided 2026-09-21: neither ever counts towards dekningsgrad), and `belop` is rounded
// per line total rather than per unit (see brfOmsetning's backend comment), so it carries each
// order's truncation error into the total. Summing kroner avoids both.
function brfProsentOf(orders: NO05[]): number {
    const omsetning = orders.reduce((s, o) => s + o.brfOmsetning, 0);
    if (omsetning === 0) return 0;
    const kost = orders.reduce((s, o) => s + o.brfKost, 0);
    return ((omsetning - kost) / omsetning) * 100;
}

const sumBrf = (orders: NO05[]) => ({
    brfOmsetning: orders.reduce((s, o) => s + o.brfOmsetning, 0),
    brfKost:      orders.reduce((s, o) => s + o.brfKost, 0),
    brfProsent:   brfProsentOf(orders),
});

export function computeStats(orders: NO05[]): NO05StatusStat[] {
    type Acc = { count: number; totalAmount: number; brfOmsetning: number; brfKost: number; antBestilt: number; antLevert: number };

    // Orders with BK='B' (retur til leverandør) are not sales — they get their own cross-status
    // box below and are excluded from every other box (Alle ordre included), so the "Total sum"
    // matches ORVI instead of being inflated by supplier returns.
    const returnToSupplier = orders.filter((o) => isReturnToSupplierBk(o.bk));

    // ORVI's "status U excluded" rule (CICSORVI.CBL:2841) only fires when the operator's
    // ORDRETYPE search parameter (COM-ORDTYPE, a whole-search-session value the operator types
    // into ORVI's mandatory ORDRETYPE box — NOT a per-order field, despite the coincidental
    // overlap with ordre_nrf's own per-order O-ORDRETYPE/OrderTypeFlag letters) is literally 'D'.
    // NO05's "alle" search corresponds to ORVI run with ORDRETYPE='S' (COM-ORDTYPE='S' sets
    // COM-SYNKE='J' — descending sort, no status/date restriction — CICSORVI.CBL:1769,3156), so
    // this exclusion never applies here and orders don't get filtered by status at all.
    const coreOrders = orders.filter((o) => !isReturnToSupplierBk(o.bk));

    const returs = coreOrders.filter((o) => o.ordreNr.startsWith('9'));

    const groups: Record<string, Acc> = {};
    for (const o of coreOrders) {
        const key = o.status;
        if (!groups[key]) groups[key] = { count: 0, totalAmount: 0, brfOmsetning: 0, brfKost: 0, antBestilt: 0, antLevert: 0 };
        groups[key].count++;
        groups[key].totalAmount  += o.belop;
        groups[key].brfOmsetning += o.brfOmsetning;
        groups[key].brfKost      += o.brfKost;
        groups[key].antBestilt   += o.antBestilt;
        groups[key].antLevert    += o.antLevert;
    }

    const toStat = (status: string, g: Acc): NO05StatusStat => ({
        status,
        count:        g.count,
        totalAmount:  g.totalAmount,
        brfOmsetning: g.brfOmsetning,
        brfKost:      g.brfKost,
        brfProsent:   g.brfOmsetning !== 0 ? ((g.brfOmsetning - g.brfKost) / g.brfOmsetning) * 100 : 0,
        antBestilt:   g.antBestilt,
        antLevert:    g.antLevert,
    });

    const allStat: NO05StatusStat = {
        status:      'Alle ordre',
        count:       coreOrders.length,
        totalAmount: coreOrders.reduce((s, o) => s + o.belop, 0),
        ...sumBrf(coreOrders),
        antBestilt:  coreOrders.reduce((s, o) => s + o.antBestilt, 0),
        antLevert:   coreOrders.reduce((s, o) => s + o.antLevert, 0),
    };

    const stats: NO05StatusStat[] = [allStat];

    for (const name of PREFERRED_STATUS_ORDER) {
        if (groups[name]) {
            stats.push(toStat(name, groups[name]));
            delete groups[name];
        }
    }
    for (const [name, g] of Object.entries(groups)) {
        stats.push(toStat(name, g));
    }

    if (returs.length > 0) {
        stats.push({
            status:      'Kredit',
            count:       returs.length,
            totalAmount: returs.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(returs),
            antBestilt:  returs.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   returs.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const kontant = coreOrders.filter((o) => KONTANT_PAYMENT_METHODS.includes(o.betalingsKode));
    if (kontant.length > 0) {
        stats.push({
            status:      'Kontant',
            count:       kontant.length,
            totalAmount: kontant.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(kontant),
            antBestilt:  kontant.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   kontant.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const direkte = coreOrders.filter((o) => DIREKTE_ORDER_TYPES.includes(o.direkteFlagg));
    if (direkte.length > 0) {
        stats.push({
            status:      'Direkte ordre',
            count:       direkte.length,
            totalAmount: direkte.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(direkte),
            antBestilt:  direkte.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   direkte.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const plukk = coreOrders.filter((o) => isPlukkOrdre(o.skaff));
    if (plukk.length > 0) {
        stats.push({
            status:      'Plukkordre',
            count:       plukk.length,
            totalAmount: plukk.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(plukk),
            antBestilt:  plukk.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   plukk.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    const bkCode = coreOrders.filter((o) => hasBkCode(o.bk));
    if (bkCode.length > 0) {
        stats.push({
            status: 'Ordre med BK kode',
            count:       bkCode.length,
            totalAmount: bkCode.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(bkCode),
            antBestilt:  bkCode.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   bkCode.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    if (returnToSupplier.length > 0) {
        stats.push({
            status: 'Retur til leverandør',
            count:       returnToSupplier.length,
            totalAmount: returnToSupplier.reduce((s, o) => s + o.belop, 0),
            ...sumBrf(returnToSupplier),
            antBestilt:  returnToSupplier.reduce((s, o) => s + o.antBestilt, 0),
            antLevert:   returnToSupplier.reduce((s, o) => s + o.antLevert, 0),
        });
    }

    return stats;
}

// ---- Ordredato boxes ----------------------------------------------------------------------

/** Selected-date key for the "Eldre enn 7 dager" box (any order dated before the 7 day boxes). */
export const OLDER_DATE_KEY = 'eldre';

export const DATE_BUCKET_DAYS = 7;

// Fixed list rather than toLocaleDateString's weekday: 'short', which gives 'ons.' with a dot.
const WEEKDAY_SHORT = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];

/** Local-time yyyy-MM-dd, the same shape the backend sends orderDate in. */
const toDateKey = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** orderDate arrives as 'yyyy-MM-dd' — compare as strings, no Date parsing (avoids UTC shifts). */
const orderDateKey = (o: NO05) => (o.dato ?? '').substring(0, 10);

export interface DateBucket {
    key: string;
    label: string;
    count: number;
    totalAmount: number;
}

/** Today, yesterday and the 5 days before (newest first), then everything older. */
export function computeDateBuckets(orders: NO05[], today: Date = new Date()): DateBucket[] {
    // Same population as "Alle ordre": BK='B' (retur til leverandør) is not a sale, so leave it
    // out of the counts/sums here too — otherwise the day boxes wouldn't add up to Alle ordre.
    const core = orders.filter((o) => !isReturnToSupplierBk(o.bk));
    const fmt = (d: Date) => d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: '2-digit' });

    const buckets: DateBucket[] = [];
    for (let i = 0; i < DATE_BUCKET_DAYS; i++) {
        const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
        const key = toDateKey(d);
        const prefix = i === 0 ? 'I dag'
            : i === 1 ? 'I går'
            : WEEKDAY_SHORT[d.getDay()];
        const matching = core.filter((o) => orderDateKey(o) === key);
        buckets.push({
            key,
            label: `${prefix} ${fmt(d)}`,
            count: matching.length,
            totalAmount: matching.reduce((s, o) => s + o.belop, 0),
        });
    }

    const oldestKey = buckets[buckets.length - 1].key;
    const older = core.filter((o) => orderDateKey(o) < oldestKey);
    buckets.push({
        key: OLDER_DATE_KEY,
        label: `Eldre enn ${DATE_BUCKET_DAYS} dager`,
        count: older.length,
        totalAmount: older.reduce((s, o) => s + o.belop, 0),
    });
    return buckets;
}

/** Orders belonging to the selected Ordredato box ('' = no date filter). */
export function filterByDate(orders: NO05[], dateKey: string, today: Date = new Date()): NO05[] {
    if (!dateKey) return orders;
    if (dateKey === OLDER_DATE_KEY) {
        const oldest = toDateKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() - (DATE_BUCKET_DAYS - 1)));
        return orders.filter((o) => orderDateKey(o) < oldest);
    }
    return orders.filter((o) => orderDateKey(o) === dateKey);
}
